import React, {useState} from 'react'
import {useSwipeable} from 'react-swipeable'
import {CSSObject} from '@emotion/styled'
import {Scholarship} from 'gatsby-graphql-types-generated'

import {Box, Flex} from 'src/components/Box'
import {Header2} from 'src/components/text'
import {GreenYellowUnderlineV2} from 'src/components/emphasis'
import {ScholarshipCardDesktop} from 'src/components/ScholarshipCard'
// import ResourceCard from 'src/components/ScholarshipCard/ResourceCard'
import {
  ArrowIconToLeft,
  ArrowIconToRight,
  FilledIndicatorIcon,
  HollowIndicatorIcon,
} from 'src/components/icons'
import MobileFeatured from 'src/components/ScholarshipCard/MobileFeatured'
import {containerHoverShadow, containerShadow} from 'src/styles/shadow'
import * as styles from 'src/styles'

import {Action} from './Action'

const prevNextCss: CSSObject = {
  background: 'white',
  borderRadius: '50%',
  height: '50px',
  width: '50px',
  ...containerShadow,
  transition: styles.animation.hoverEnter('box-shadow'),
  ':hover:enabled': {
    ...containerHoverShadow,
    transition: styles.animation.hoverLeave('box-shadow'),
  },
  ':disabled': {
    opacity: 0.4,
  },
}

type Props = {
  heading?: string
  data: [Scholarship]
}

const DesktopCarousel = ({heading, data}: Props) => {
  const MAX_WIDTH = 538 + 60
  const [currentIndex, setCurrentIndex] = useState(0)
  const moveRight = () =>
    setCurrentIndex(Math.min(currentIndex + 1, data.length - 1))
  const moveLeft = () => setCurrentIndex(Math.max(0, currentIndex - 1))
  const handlers = useSwipeable({
    onSwipedLeft: () => moveRight(),
    onSwipedRight: () => moveLeft(),
  })
  return (
    <Box mb={8}>
      <Flex flexDirection="row" justifyContent="space-between">
        <Header2 mb={5}>
          <GreenYellowUnderlineV2>{heading}</GreenYellowUnderlineV2>
        </Header2>
        <Flex flexDirection="row" gridColumnGap={5}>
          <Action
            css={{
              ...prevNextCss,
            }}
            disabled={currentIndex === 0}
            aria-label="Previous"
            onClick={() => moveLeft()}
          >
            <ArrowIconToLeft color="grey5" />
          </Action>
          <Action
            css={{
              ...prevNextCss,
            }}
            disabled={currentIndex === data.length - 1}
            aria-label="Next"
            onClick={() => moveRight()}
          >
            <ArrowIconToRight color="grey5" />
          </Action>
        </Flex>
      </Flex>
      <Flex
        {...handlers}
        flexDirection="row"
        alignItems="center"
        gridColumnGap="60px"
        css={{
          transform: `translateX(-${MAX_WIDTH * currentIndex}px)`,
          transition: 'all ease 0.4s',
        }}
      >
        {data.map((item, index) => {
          let component
          if (item.__typename === 'ContentfulScholarships') {
            const formattedDate = new Intl.DateTimeFormat('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }).format(new Date(item.closeDate))
            component = (
              <div key={index} onClick={() => setCurrentIndex(index)}>
                <ScholarshipCardDesktop
                  isDisabled={false}
                  title={item.programName}
                  description={
                    item.programSelfDescription?.programSelfDescription
                  }
                  source={item.organizationName}
                  price={item.awardAmount}
                  date={formattedDate}
                  applyLink={item.applicationUrl?.applicationUrl}
                  featuredImageUrl={item.featuredImageUrl?.featuredImageUrl}
                />
              </div>
            )
          }
          // else {
          //   component = (
          //     <div onClick={() => setCurrentIndex(index)}>
          //       <ResourceCard
          //         isDisabled={false}
          //         title={item.title}
          //         description={item.description}
          //         source={item.source}
          //       />
          //     </div>
          //   )
          // }
          return component
        })}
      </Flex>
    </Box>
  )
}

const MobileCarousel = ({data}: Props) => {
  const MAX_WIDTH = 290
  const [currentIndex, setCurrentIndex] = useState(0)

  const handlers = useSwipeable({
    onSwipedLeft: () =>
      setCurrentIndex(Math.min(currentIndex + 1, data.length - 1)),
    onSwipedRight: () => setCurrentIndex(Math.max(0, currentIndex - 1)),
  })
  return (
    <>
      <Box
        css={{
          width: MAX_WIDTH,
          overflow: 'hidden',
          borderRadius: '15px',
          boxShadow: '0px 4px 20px 0px #14242c40',
        }}
      >
        <ul
          {...handlers}
          css={{
            display: 'flex',
            flexDirection: 'row',
            transform: `translateX(-${MAX_WIDTH * currentIndex}px)`,
            transition: 'all 0.4s',
          }}
        >
          {data.map((item, index) => {
            const formattedDate = new Intl.DateTimeFormat('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }).format(new Date(item.closeDate))
            return (
              <li key={index}>
                <MobileFeatured
                  title={item.programName}
                  source={item.organizationName}
                  price={item.awardAmount}
                  date={formattedDate}
                  applyLink={item.applicationUrl?.applicationUrl}
                  featuredImageUrl={item.featuredImageUrl?.featuredImageUrl}
                />
              </li>
            )
          })}
        </ul>
      </Box>
      <Flex flexDirection="row" justifyContent="center" mt={3} gridGap={1}>
        {data.map((_, index) =>
          index === currentIndex ? (
            <FilledIndicatorIcon key={index} />
          ) : (
            <HollowIndicatorIcon key={index} />
          )
        )}
      </Flex>
    </>
  )
}

const ScholarshipFeaturedCarousel = ({heading, data}: Props) => {
  return (
    <>
      <Box mt={4} display={{default: 'none', md: 'block'}}>
        <DesktopCarousel heading={heading} data={data} />
      </Box>
      <Box mt={4} display={{default: 'block', md: 'none'}} alignSelf="center">
        <MobileCarousel data={data} />
      </Box>
    </>
  )
}

export default ScholarshipFeaturedCarousel
